/**
 * IndexedDB persistence for notification center read/dismissed state.
 *
 * Replaces localStorage-based notificationCenterPersistence.ts.
 * Storage: browser-managed quota (GB-scale), vs 5 MB shared localStorage quota.
 * Eviction: timestamp-based, mirroring Ably message retention window.
 */

const DB_NAME = 'scalar:notificationCenter';
const DB_VERSION = 1;
const STORE_NAME = 'flags';

/**
 * Mirror Ably message retention window.
 * Current plan: 1 day. Update to 30 * 24 * 60 * 60 * 1000 after upgrading the Ably plan.
 */
export const ABLY_RETENTION_MS = 24 * 60 * 60 * 1000;

/** localStorage key prefix used by the old v1 persistence layer. */
const LS_V1_KEY_PREFIX = 'scalar:notificationCenter:v1:user:';

interface FlagRecord {
  /** Composite key: `${userId}:${id}` — used as IDB keyPath. */
  key: string;
  userId: number;
  id: string;
  type: 'read' | 'dismissed';
  /** Date.now() when the flag was persisted; used for expiry. */
  ts: number;
}

// ── DB singleton ─────────────────────────────────────────────────────────────

let iDb: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (iDb) return iDb;
  iDb = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('by_userId', 'userId', { unique: false });
        store.createIndex('by_ts', 'ts', { unique: false });
      }
    };
    req.onsuccess = e => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror = () => {
      iDb = null; // allow retry
      reject(req.error);
    };
  });
  return iDb;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function makeKey(userId: number, id: string): string {
  return `${userId}:${id}`;
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Load all non-expired read/dismissed flags for a user.
 * Call once on mount and store the result in a ref for sync access during message handling.
 * dismissed maps id → ts (epoch ms when the user acknowledged the notification).
 */
export async function loadFlagsForUser(userId: number): Promise<{ read: Set<string>; dismissed: Map<string, number> }> {
  const result = { read: new Set<string>(), dismissed: new Map<string, number>() };
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.objectStore(STORE_NAME).index('by_userId');
    const records = await idbRequest<FlagRecord[]>(index.getAll(IDBKeyRange.only(userId)));
    const cutoff = Date.now() - ABLY_RETENTION_MS;
    records.forEach(r => {
      if (r.ts < cutoff) {
        return;
      }
      if (r.type === 'read') {
        result.read.add(r.id);
        return;
      }
      result.dismissed.set(r.id, r.ts);
    })
  } catch {
    // IndexedDB unavailable (private mode, quota error) — fall back to empty sets.
    // In-memory React state still works; flags just won't survive a reload.
  }
  return result;
}

export async function persistRead(userId: number, id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).put({
      key: makeKey(userId, id), userId, id, type: 'read', ts: Date.now(),
    } satisfies FlagRecord));
  } catch {
    // no-op
  }
}

export async function persistDismissed(userId: number, id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).put({
      key: makeKey(userId, id), userId, id, type: 'dismissed', ts: Date.now(),
    } satisfies FlagRecord));
  } catch {
    // no-op
  }
}

export async function persistDismissedMany(userId: number, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const ts = Date.now();
    await Promise.all(
      ids.map(id =>
        idbRequest(store.put({
          key: makeKey(userId, id), userId, id, type: 'dismissed', ts,
        } satisfies FlagRecord))
      )
    );
  } catch {
    // no-op
  }
}

/**
 * Delete all records older than ABLY_RETENTION_MS.
 * Fire-and-forget on mount — keeps the store bounded without blocking the UI.
 */
export async function evictExpired(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const cutoff = Date.now() - ABLY_RETENTION_MS;
    const keys = await idbRequest<IDBValidKey[]>(
      store.index('by_ts').getAllKeys(IDBKeyRange.upperBound(cutoff))
    );
    await Promise.all(keys.map(k => idbRequest(store.delete(k))));
  } catch {
    // no-op
  }
}

/**
 * One-time migration from localStorage v1 format.
 * Imports existing IDs with ts = Date.now() (they will expire after ABLY_RETENTION_MS).
 * Removes the localStorage key after a successful import.
 */
export async function migrateFromLocalStorage(userId: number): Promise<void> {
  if (typeof window === 'undefined') return;
  const lsKey = `${LS_V1_KEY_PREFIX}${userId}`;
  try {
    const raw = window.localStorage.getItem(lsKey);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { read?: unknown; dismissed?: unknown };
    const readIds = Array.isArray(parsed.read) ? (parsed.read as unknown[]).filter((v): v is string => typeof v === 'string') : [];
    const dismissedIds = Array.isArray(parsed.dismissed) ? (parsed.dismissed as unknown[]).filter((v): v is string => typeof v === 'string') : [];
    if (readIds.length === 0 && dismissedIds.length === 0) {
      window.localStorage.removeItem(lsKey);
      return;
    }
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const ts = Date.now();
    await Promise.all([
      ...readIds.map(id => idbRequest(store.put({ key: makeKey(userId, id), userId, id, type: 'read', ts } satisfies FlagRecord))),
      ...dismissedIds.map(id => idbRequest(store.put({ key: makeKey(userId, id), userId, id, type: 'dismissed', ts } satisfies FlagRecord))),
    ]);
    window.localStorage.removeItem(lsKey);
  } catch {
    // best-effort migration — if it fails, old data stays in localStorage harmlessly
  }
}
